<template>
  <v-layout column>
    <v-card>
      <div class="topology-container">
        <div class="topology" ref="topology"></div>
        <v-menu
          v-model="tooltipVisible"
          :position-x="x"
          :position-y="y"
          absolute
        >
          <app-topology-tooltip v-if="hoveredNode" :data="[hoveredNode]"></app-topology-tooltip>
        </v-menu>
        <div class="pending" v-if="pending">
          <v-progress-circular
            :size="70"
            :width="7"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </div>
      <div class="namespace right pa-2">
        <v-select
          v-if="namespaces.length > 1"
          v-model="namespace"
          :items="namespaces"
          :label="$t('namespaces.label')"
          hide-details
          hide-selected
        />
        <v-subheader v-else-if="namespaces.length === 1">{{ $t('namespaces.current') }}: {{ namespaces[0] }}</v-subheader>
      </div>
    </v-card>
  </v-layout>
</template>

<script>
import NetworkService from '@/services/network'
import { build as buildTopology } from '@/services/topology'
import { delayUntilVisible } from '@/services/utils'
import { configure as configureTopology, fitViewport } from '@/services/vis'
import TooltipComponent from '@/components/Tooltip.vue'

export default {
  data () {
    return {
      nodes: null,
      labelLength: 20,
      topology: null,
      pending: true,
      namespace: 'default',
      namespaces: [],
      hoveredNode: null,
      tooltipVisible: false,
      tooltipTimeout: null,
      x: 0,
      y: 0
    }
  },
  watch: {
    namespace (newNamespace) {
      localStorage.namespace = newNamespace
      this.reloadTopology()
    }
  },
  created () {
    if (localStorage.namespace) {
      this.namespace = localStorage.namespace
    }
    this.namespaces = [ this.namespace ]
  },
  mounted () {
    // the following delay is required for the topology positioning to properly apply
    this.loadTopology()
      .then(() => delayUntilVisible(this.$refs, 'topology'))
      .then(() => fitViewport(this.topology))
      .catch(() => { /* discard: component not active anymore */ })
      .then(() => this.fetchNamespaces())

    this.$root.$on('wizardClose', () => {
      this.reloadTopology()
    })
  },
  destroyed () {
    this.hideTooltip()
  },
  methods: {
    loadTopology () {
      this.pending = true
      return NetworkService.nodes(this.namespace)
        .then((data) => buildTopology(data))
        .then(({ nodes, edges }) => {
          this.topology = configureTopology(this.$refs.topology, nodes, this.labelLength, edges, {
            node: {
              over: (event, id) => {
                this.showTooltip(event, id)
                if (!this.nodes) {
                  this.nodes = {}
                  nodes.forEach((node) => {
                    this.nodes[node.id] = node
                  })
                }
              },
              out: () => this.hideTooltip()
            }
          })
        }).catch(() => {
          // can happen if the user switches page too quickly
        }).finally(() => {
          this.pending = false
        })
    },
    reloadTopology () {
      if (this.topology) {
        this.nodes = null
        this.topology.destroy()
        this.loadTopology()
      }
    },
    fetchNamespaces () {
      NetworkService.namespaces().then((data) => {
        this.namespaces = data || []
        // if the selected namespace doesn't exist anymore, pick the first one
        if (this.namespaces.length > 0 && !this.namespaces.includes(this.namespace)) {
          this.namespace = this.namespaces[0]
        }
      })
    },
    showTooltip (event, id) {
      this.x = event.clientX
      this.y = event.clientY
      this.tooltipTimeout = setTimeout(() => {
        this.hoveredNode = this.nodes[id]
        this.tooltipVisible = true
      }, 300)
    },
    hideTooltip () {
      this.tooltipVisible = false
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout)
        this.tooltipTimeout = null
      }
    }
  },
  components: {
    'app-topology-tooltip': TooltipComponent
  }
}
</script>

<style lang="scss" scoped>
  .v-card, .topology-container {
    height: 100%;
    position: relative;
  }

  .topology-container > .topology {
    bottom: 1px;
    left: 1px;
    position: absolute;
    right: 1px;
    top: 1px;
  }

  .topology ::v-deep .vis-network {
    outline: none;
  }

  .namespace {
    background: white;
    position: absolute;
    right: 0;
    top: 0;
  }

  .pending {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    text-align: center;

    > .v-progress-circular {
      top: calc(50% - 35px);
    }
  }
</style>

<i18n>
{
  "en": {
    "title": "Topology",
    "namespaces": {
      "current": "Current namespace",
      "label": "Selected namespace"
    }
  }
}
</i18n>
